import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '@/features/utils'

/**
 * Horizontal section tabs: active tab uses bold dark text + blue underline;
 * inactive tabs are muted. A grey rule runs under the tab labels only (not edge-to-edge).
 *
 * Composition API (full control):
 * @example
 * <UnderlineTabs defaultValue="a">
 *   <UnderlineTabsList>
 *     <UnderlineTabsTrigger value="a">Options</UnderlineTabsTrigger>
 *     <UnderlineTabsTrigger value="b">Billing</UnderlineTabsTrigger>
 *   </UnderlineTabsList>
 *   <UnderlineTabsContent value="a">…</UnderlineTabsContent>
 *   <UnderlineTabsContent value="b">…</UnderlineTabsContent>
 * </UnderlineTabs>
 *
 * Config array (common case):
 * @example
 * <UnderlineTabbedSections
 *   defaultValue="options"
 *   tabs={[
 *     { value: 'options', label: 'Options', content: <OptionsPanel /> },
 *     { value: 'billing', label: 'Billing', content: <BillingPanel /> },
 *   ]}
 * />
 */
const UnderlineTabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} className={cn('tw-min-w-0', className)} {...props} />
))
UnderlineTabs.displayName = 'UnderlineTabs'

const UnderlineTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  /* Scrollport can be full width; border-b sits on w-max row so the line ends after the last tab */
  <div
    className={cn(
      'tw-max-w-full tw-overflow-x-auto tw-overflow-y-hidden tw-overscroll-x-contain',
      className
    )}
  >
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'tw-inline-flex tw-w-max tw-max-w-none tw-flex-nowrap tw-items-end tw-gap-x-5 sm:tw-gap-x-6',
        'tw-border-b tw-border-slate-200 tw-bg-transparent tw-shadow-none tw-px-0'
      )}
      {...props}
    />
  </div>
))
UnderlineTabsList.displayName = 'UnderlineTabsList'

const UnderlineTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'tw-shrink-0 tw-border-b-2 tw-border-transparent tw-bg-transparent tw-pb-2.5 tw-pt-3 tw-text-sm',
      'tw-font-normal tw-text-slate-500 tw-transition-colors',
      '-tw-mb-px tw-whitespace-nowrap',
      'hover:tw-text-slate-700',
      'focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500 focus-visible:tw-ring-offset-2',
      'disabled:tw-pointer-events-none disabled:tw-opacity-40',
      'data-[state=active]:tw-border-blue-600 data-[state=active]:tw-font-semibold data-[state=active]:tw-text-slate-900',
      className
    )}
    {...props}
  />
))
UnderlineTabsTrigger.displayName = 'UnderlineTabsTrigger'

const UnderlineTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'tw-mt-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500 focus-visible:tw-ring-offset-2',
      className
    )}
    {...props}
  />
))
UnderlineTabsContent.displayName = 'UnderlineTabsContent'

export type UnderlineTabItem = {
  value: string
  label: React.ReactNode
  content: React.ReactNode
  disabled?: boolean
}

export type UnderlineTabbedSectionsProps = {
  tabs: UnderlineTabItem[]
  /** Uncontrolled default */
  defaultValue?: string
  /** Controlled */
  value?: string
  onValueChange?: (value: string) => void
  className?: string
  listClassName?: string
  contentClassName?: string
}

export function UnderlineTabbedSections({
  tabs,
  defaultValue,
  value,
  onValueChange,
  className,
  listClassName,
  contentClassName,
}: UnderlineTabbedSectionsProps) {
  const fallback = tabs[0]?.value ?? ''
  return (
    <UnderlineTabs
      className={className}
      defaultValue={value === undefined ? (defaultValue ?? fallback) : undefined}
      value={value}
      onValueChange={onValueChange}
    >
      <UnderlineTabsList className={listClassName}>
        {tabs.map((tab) => (
          <UnderlineTabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled}>
            {tab.label}
          </UnderlineTabsTrigger>
        ))}
      </UnderlineTabsList>
      {tabs.map((tab) => (
        <UnderlineTabsContent key={tab.value} value={tab.value} className={contentClassName}>
          {tab.content}
        </UnderlineTabsContent>
      ))}
    </UnderlineTabs>
  )
}

export { UnderlineTabs, UnderlineTabsList, UnderlineTabsTrigger, UnderlineTabsContent }
